<template>
  <v-card class="base-filter-card pa-4" elevation="1">
    <v-btn
      v-if="showExpandIcon"
      class="expand-btn elevation-1"
      :class="{ 'expand-btn--hovered': isHoveringExpandButton }"
      min-width="32"
      height="32"
      @mouseenter="isHoveringExpandButton = true"
      @mouseleave="isHoveringExpandButton = false"
      @click="toggleExpanded"
    >
      <span v-if="isHoveringExpandButton" class="expand-btn-text">
        {{ expandButtonHoverLabel }}
      </span>
      <v-icon color="primary-accent" size="20">
        {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </v-btn>

    <v-row class="bi-filter-row">
      <slot
        name="always-visible"
        :trigger-search="triggerSearch"
        :trigger-clear="triggerClear"
      ></slot>
    </v-row>

    <v-expand-transition>
      <div v-show="isExpanded">
        <v-row v-if="$slots.expanded" class="bi-filter-row bi-filter-row--expanded">
          <slot name="expanded"></slot>
        </v-row>

        <div class="bi-filter-actions">
          <template v-if="$slots.actions">
            <slot
              name="actions"
              :trigger-search="triggerSearch"
              :trigger-clear="triggerClear"
            ></slot>
          </template>

          <v-row v-else-if="showActions" class="bi-filter-actions-row">
            <v-col cols="12" class="d-flex justify-end align-center bi-filter-actions-col">
              <v-btn outlined style="height: 42px; width: 165px; border-radius: 6px; border-color: var(--v-muted-accent-base);" color="muted-accent" @click="triggerClear">
                <v-icon left size="18">mdi-broom</v-icon>
                {{ $tc('common.clearFilter', 2) }}
              </v-btn>
              <v-btn elevation="0" color="primary" style="height: 42px; width: 165px; border-radius: 6px;" @click="triggerSearch">
                <v-icon left size="18">mdi-magnify</v-icon>
                {{ $tc('common.search', 2) }}
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
export default {
  name: 'ExpansionPanel',
  props: {
    startExpanded: {
      type: Boolean,
      default: false,
    },
    showExpandIcon: {
      type: Boolean,
      default: true,
    },
    expandButtonHoverTextExpanded: {
      type: String,
      default: '',
    },
    expandButtonHoverTextCollapsed: {
      type: String,
      default: '',
    },
    autoSearchOnAlwaysVisibleChange: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    alwaysVisibleValues: {
      type: [Object, Array, String, Number, Boolean],
      default: null,
    },
    searchAction: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      isExpanded: this.startExpanded,
      isHoveringExpandButton: false,
      isAlwaysVisibleWatcherReady: false,
      suppressAutoSearch: false,
    };
  },
  watch: {
    isExpanded(newValue) {
      this.$emit('on-expand-change', newValue);
    },
    alwaysVisibleValues: {
      deep: true,
      handler() {
        if (!this.autoSearchOnAlwaysVisibleChange) {
          return;
        }

        if (!this.isAlwaysVisibleWatcherReady || this.suppressAutoSearch) {
          return;
        }

        this.triggerSearch();
      },
    },
  },
  computed: {
    expandButtonHoverLabel() {
      if (this.isExpanded) {
        return this.expandButtonHoverTextExpanded || this.$t('biControlPanel.filters.panelExpanded').toString();
      }

      return this.expandButtonHoverTextCollapsed || this.$t('biControlPanel.filters.panelCollapsed').toString();
    },
  },
  mounted() {
    this.$emit('on-expand-change', this.isExpanded);
    this.isAlwaysVisibleWatcherReady = true;
  },
  methods: {
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    triggerSearch() {
      if (typeof this.searchAction === 'function') {
        this.searchAction();
        return;
      }

      this.$emit('on-search');
    },
    triggerClear() {
      this.suppressAutoSearch = true;
      this.$emit('on-clear');

      this.$nextTick(() => {
        this.suppressAutoSearch = false;
      });
    },
  },
};
</script>

<style scoped>
.base-filter-card {
  position: relative;
  overflow: visible !important;
  border-color: #e0e0e0 !important;
  border-radius: 4px;
}

.bi-filter-row {
  margin: -8px;
}

.bi-filter-row--expanded {
  margin-top: 4px;
}

.base-filter-card ::v-deep .bi-filter-row > .col,
.base-filter-card ::v-deep .bi-filter-row > [class*="col-"] {
  padding: 8px;
}

.bi-filter-actions {
  margin-top: 12px;
}

.bi-filter-actions-row {
  margin: 0;
}

.bi-filter-actions-col {
  gap: 16px;
  padding: 0;
}

.expand-btn {
  position: absolute;
  top: -16px;
  right: -16px;
  z-index: 4;
  display: flex;
  align-items: center;
  background-color: white !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 16px !important;
  padding: 0 6px !important;
  transition: padding 0.15s ease;
}

.expand-btn--hovered {
  padding: 0 10px 0 6px !important;
}

.expand-btn-text {
  margin-left: 6px;
  color: #5f6368;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}
</style>
